import { Box, Button, CircularProgress, Dialog, IconButton, Typography } from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";
import { black, greyEnabled, purple, purpleLight, red, redLight } from "../../theme/colors";

// Generic confirmation dialog, styled to match the Filters/Sort dialogs
// across the app (same icon-box + title + close button header, same
// FilterDialogFooter action row). `destructive` swaps the icon/confirm
// button to the red palette for delete-style actions.
export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message,
  confirmLabel = "Confirm",
  destructive = false,
  loading = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message: string;
  confirmLabel?: string;
  destructive?: boolean;
  loading?: boolean;
}) {
  const iconColor = destructive ? red : purple;
  const iconBg = destructive ? redLight : purpleLight;

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ minWidth: 340, maxWidth: "90vw", p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: iconBg,
                flexShrink: 0,
              }}
            >
              {destructive ? (
                <DeleteOutlineRounded sx={{ color: iconColor, fontSize: 20 }} />
              ) : (
                <HelpOutlineRounded sx={{ color: iconColor, fontSize: 20 }} />
              )}
            </Box>
            <Typography fontWeight={700} fontSize={17} color={black}>
              {title}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ "&:hover": { bgcolor: purpleLight } }}>
            <CloseRounded fontSize="small" sx={{ color: "#5B4A5A" }} />
          </IconButton>
        </Box>

        <Typography variant="body2" color={greyEnabled}>
          {message}
        </Typography>

        <Box mt={2} display="flex" gap={1.5}>
          <Button
            fullWidth
            variant="outlined"
            disabled={loading}
            onClick={onClose}
            sx={{
              borderColor: purple,
              color: purple,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { borderColor: purple, bgcolor: purpleLight },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            disabled={loading}
            onClick={onConfirm}
            sx={{
              bgcolor: destructive ? red : purple,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: destructive ? "#A32020" : "#7A2074" },
            }}
          >
            {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : confirmLabel}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
